import React, { useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, CheckCircle2, Lock, Unlock, Power, Clock } from "lucide-react";
import { api, formatError } from "../lib/api";

const STATUS_COPY = {
  off: { label: "Check-in off", cls: "bg-white/5 text-zinc-400 border-white/10" },
  open: { label: "Check-in open", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30 animate-pulse" },
  closed: { label: "Check-in closed", cls: "bg-amber-500/10 text-amber-300 border-amber-400/30" },
};

function AdminControls({ tournament, onChange }) {
  const [busy, setBusy] = useState(false);
  const status = tournament.checkin_status || "off";
  const setStatus = async (next) => {
    if (next === "off" && !window.confirm("Disable check-in? All check-in marks will be cleared.")) return;
    setBusy(true);
    try {
      await api.post(`/tournaments/${tournament.id}/checkin/status`, { status: next });
      toast.success(next === "open" ? "Check-in opened" : next === "closed" ? "Check-in closed" : "Check-in disabled");
      await onChange();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };
  const btn = "btn-ghost-glass !py-1.5 !px-3 !text-xs disabled:opacity-40";
  return (
    <div className="flex gap-2 flex-wrap">
      {status !== "open" && (
        <button disabled={busy} onClick={() => setStatus("open")} data-testid="checkin-open-btn" className="btn-liquid !py-1.5 !px-3 !text-xs">
          <Unlock size={12} /> {status === "closed" ? "Reopen check-in" : "Open check-in"}
        </button>
      )}
      {status === "open" && (
        <button disabled={busy} onClick={() => setStatus("closed")} data-testid="checkin-close-btn" className={btn}>
          <Lock size={12} /> Close check-in
        </button>
      )}
      {status !== "off" && (
        <button disabled={busy} onClick={() => setStatus("off")} data-testid="checkin-disable-btn" className={`${btn} !text-zinc-400`}>
          <Power size={12} /> Disable
        </button>
      )}
    </div>
  );
}

function CaptainAction({ tournament, myTeam, onChange }) {
  const [busy, setBusy] = useState(false);
  const status = tournament.checkin_status || "off";
  if (!myTeam || status === "off") return null;
  if (myTeam.checked_in) {
    return (
      <div data-testid="checkin-done" className="inline-flex items-center gap-2 text-sm text-emerald-300 font-semibold">
        <CheckCircle2 size={16} /> {myTeam.name} is checked in
      </div>
    );
  }
  if (status === "closed") {
    return (
      <div data-testid="checkin-missed" className="inline-flex items-center gap-2 text-sm text-amber-300">
        <Clock size={16} /> Check-in closed — your team is not confirmed. Contact an admin.
      </div>
    );
  }
  const checkIn = async () => {
    setBusy(true);
    try {
      await api.post(`/tournaments/${tournament.id}/checkin`);
      toast.success("You're checked in!");
      await onChange();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };
  return (
    <button onClick={checkIn} disabled={busy} data-testid="checkin-captain-btn" className="btn-liquid !px-6">
      <ClipboardCheck size={14} /> {busy ? "Checking in..." : `Check in ${myTeam.name}`}
    </button>
  );
}

export const CheckInPanel = ({ tournament, teams, user, canEdit, onChange }) => {
  const status = tournament.checkin_status || "off";
  const preLive = !["live", "finished"].includes(tournament.status);
  if (!preLive || (status === "off" && !canEdit)) return null;
  const myTeam = user ? teams.find((t) => t.owner_id === user.id) : null;
  const done = teams.filter((t) => t.checked_in).length;
  const copy = STATUS_COPY[status] || STATUS_COPY.off;
  return (
    <div data-testid="checkin-panel" className="glass-dark rounded-2xl p-5 mb-4 border border-white/5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <span data-testid="checkin-status-badge"
            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] uppercase tracking-widest font-mono border ${copy.cls}`}>
            <ClipboardCheck size={11} /> {copy.label}
          </span>
          {status !== "off" && (
            <span data-testid="checkin-count" className="font-mono text-xs text-zinc-400">
              <span className="text-white font-bold">{done}</span> / {teams.length} confirmed
            </span>
          )}
        </div>
        {canEdit && <AdminControls tournament={tournament} onChange={onChange} />}
      </div>
      {status === "off" && canEdit && (
        <p className="text-xs text-zinc-500 mt-3">Open check-in so captains confirm attendance before the tournament starts.</p>
      )}
      {status !== "off" && (
        <div className="mt-4"><CaptainAction tournament={tournament} myTeam={myTeam} onChange={onChange} /></div>
      )}
    </div>
  );
};

export const CheckInBadge = ({ team, tournament, canEdit, onChange }) => {
  const [busy, setBusy] = useState(false);
  const status = tournament.checkin_status || "off";
  if (status === "off" || ["live", "finished"].includes(tournament.status)) return null;
  const ok = Boolean(team.checked_in);
  const toggle = async () => {
    setBusy(true);
    try {
      await api.put(`/teams/${team.id}/checkin`, { checked_in: !ok });
      toast.success(`${team.name} ${ok ? "marked not confirmed" : "checked in"}`);
      await onChange();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };
  const cls = `inline-flex items-center justify-center gap-1 min-w-[112px] px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-mono border whitespace-nowrap transition-colors ${
    ok ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border-amber-400/30"
  }`;
  const label = ok ? "Checked in" : "Not confirmed";
  if (!canEdit) return <span data-testid={`checkin-badge-${team.id}`} className={cls}>{label}</span>;
  return (
    <button onClick={toggle} disabled={busy} data-testid={`checkin-toggle-${team.id}`}
      title={ok ? "Click to mark as not confirmed" : "Click to check in manually"}
      className={`${cls} hover:brightness-125 disabled:opacity-50`}>
      {ok ? <CheckCircle2 size={10} /> : <Clock size={10} />} {label}
    </button>
  );
};
